import {
  AccountEditPlan,
  Bucket,
  BucketType,
  defaultChartData,
  Portfolio,
} from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import {
  API_ACCOUNT_DETAIL_PLAN,
  API_ACCOUNT_DETAIL_PLAN_HEDGING,
  API_ACCOUNT_DETAIL_PLAN_REBALANCING,
  API_ACCOUNT_DETAIL_PLAN_REBALANCING_TIME,
  API_ACCOUNT_EDIT_PLAN,
  API_ACCOUNT_PLAN_HEDGING,
  API_ACCOUNT_PLAN_REBALANCING,
  API_ACCOUNT_PLAN_REBALANCING_TIME,
  API_ACCOUNT_QUESTIONNAIRE_PROFILE,
  API_ACCOUNT_QUESTIONNAIRE_PROFILE_DETAIL,
  API_ACCOUNT_PLAN_ENDDATE,
  API_ACCOUNT_DETAIL_PLAN_ENDDATE,
  API_ACCOUNT_PLAN_DISPLAYDATE,
  API_ACCOUNT_DETAIL_PLAN_DISPLAYDATE,
  API_FUNCTION_TYPE,
  API_FUNCTION_TYPE_TARGET,
  API_ACCOUNT_DETAIL_PLAN_INFLATION,
  API_ACCOUNT_PLAN_INFLATION,
  API_ACCOUNT_PLAN_PORTFOLIO,
  API_ACCOUNT_DETAIL_PLAN_PORTFOLIO,
  API_ACCOUNT_DETAIL_PLAN_HISTORY,
  API_ACCOUNT_DETAIL_PLAN_ONETIMEREBALANCE,
  API_ACCOUNT_PLAN_TOGGLE_EXTPROD,
  API_ACCOUNT_DETAIL_PLAN_TOGGLE_EXTPROD,
  API_ACCOUNT_PLAN_PORTFOLIO_UPDATE,
  API_ACCOUNT_DETAIL_PLAN_PORTFOLIO_UPDATE,
  API_ACCOUNT_PLAN_PORTFOLIO_RECOMMENDED,
  API_ACCOUNT_DETAIL_PLAN_PORTFOLIO_RECOMMENDED,
} from 'api/endpoints';
import { ACCOUNTS_FEES_EDIT } from 'routes';
import { action, computed, observable } from 'mobx';
import { PieSeriesData } from 'components/Graph/PieGraph';
import { convertPiechartData, computeChartData } from 'helpers/chart';
import { Comparison, makePortfolioComparison } from 'stores/account/overviewStore/overviewStore';

import { RootStore } from 'stores/rootStore';
import { ACCOUNTS_EDIT_INVESTMENT } from 'pages/Accounts/Edit/accountEditRoutes';
import history from 'helpers/routerHistory';

import { SingleValueResponse } from 'common/interfaces';
import { ApiResponse } from 'stores/apiStore';

export class WorkflowPlanStore {
  rootStore: RootStore;
  @observable data: AccountEditPlan | null = null;
  @observable _activatedBucketType: null | BucketType = null;
  @observable bucketTab = 0;
  accountId = '';
  endpoint: API_FUNCTION_TYPE;
  riskEndpoint: API_FUNCTION_TYPE;
  endDateEndpoint: API_FUNCTION_TYPE;
  displayDateEndpoint: API_FUNCTION_TYPE;
  rebalancingEndpoint: API_FUNCTION_TYPE;
  rebalancingTimeEndpoint: API_FUNCTION_TYPE;
  hedgingEndpoint: API_FUNCTION_TYPE;
  inflationEndpoint: API_FUNCTION_TYPE;
  esgEndpoint: API_FUNCTION_TYPE;
  extProdEndpoint: API_FUNCTION_TYPE_TARGET;
  updateEndpoint: API_FUNCTION_TYPE;
  recommendedEndpoint: API_FUNCTION_TYPE_TARGET;
  detailMode = false;

  @observable chartComputedData = defaultChartData;
  @observable inflationModal = false;
  @observable zeroDeposits = false;

  @observable editTarget: string | null = null;
  @observable editDeposit: string | null = null;
  @observable editReserve: string | null = null;
  @observable editExtProduct: string | null = null;
  @observable editRent = false;
  @observable portfolioComparison: Comparison[] = [];

  @observable fetchTimestamp = 0;

  @observable k1isins: string[] = [];

  constructor(rootStore: RootStore, accountId: string, detailMode?: boolean, userMode?: boolean) {
    this.rootStore = rootStore;
    this.accountId = accountId;
    this.endpoint = detailMode
      ? userMode
        ? API_ACCOUNT_DETAIL_PLAN_HISTORY
        : API_ACCOUNT_DETAIL_PLAN
      : API_ACCOUNT_EDIT_PLAN;
    this.riskEndpoint = detailMode ? API_ACCOUNT_QUESTIONNAIRE_PROFILE_DETAIL : API_ACCOUNT_QUESTIONNAIRE_PROFILE;
    this.rebalancingEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_REBALANCING : API_ACCOUNT_PLAN_REBALANCING;
    this.rebalancingTimeEndpoint = detailMode
      ? API_ACCOUNT_DETAIL_PLAN_REBALANCING_TIME
      : API_ACCOUNT_PLAN_REBALANCING_TIME;
    this.hedgingEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_HEDGING : API_ACCOUNT_PLAN_HEDGING;
    this.endDateEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_ENDDATE : API_ACCOUNT_PLAN_ENDDATE;
    this.displayDateEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_DISPLAYDATE : API_ACCOUNT_PLAN_DISPLAYDATE;
    this.inflationEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_INFLATION : API_ACCOUNT_PLAN_INFLATION;
    this.esgEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_PORTFOLIO : API_ACCOUNT_PLAN_PORTFOLIO;
    this.extProdEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_TOGGLE_EXTPROD : API_ACCOUNT_PLAN_TOGGLE_EXTPROD;
    this.updateEndpoint = detailMode ? API_ACCOUNT_DETAIL_PLAN_PORTFOLIO_UPDATE : API_ACCOUNT_PLAN_PORTFOLIO_UPDATE;
    this.recommendedEndpoint = detailMode
      ? API_ACCOUNT_DETAIL_PLAN_PORTFOLIO_RECOMMENDED
      : API_ACCOUNT_PLAN_PORTFOLIO_RECOMMENDED;

    if (detailMode) {
      this.detailMode = true;
    }
  }

  @action
  setAccountId = (accountId: string) => (this.accountId = accountId);

  @action setEditTarget = (targetId: string | null) => (this.editTarget = targetId);
  @action setEditDeposit = (depositId: string | null) => (this.editDeposit = depositId);
  @action setEditRent = (edit: boolean) => (this.editRent = edit);
  @action setEditReserve = (reserveId: string | null) => (this.editReserve = reserveId);
  @action setEditExtProduct = (productId: string | null) => (this.editExtProduct = productId);

  @action
  fetchAccountPlan = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    /*
    await this.fetchData(`${this.endpoint(this.accountId)}`, {
      resetData: false,
    });
    */
    const response = await this.rootStore.stores.apiStore.get<AccountEditPlan>(this.endpoint(this.accountId));
    if (response?.data) {
      this.data = response.data;
      computeChartData(this.data, this.chartComputedData);
      this.zeroDeposits =
        !this.detailMode &&
        (!this.data.depositData.initial || this.data.depositData.initial.value.value === 0) &&
        (!this.data.depositData.periodic || this.data.depositData.periodic.value.value === 0);
      this.fetchTimestamp = Date.now();

      this.data.buckets[0].portfolio.assetCategories.forEach((aCat) =>
        aCat.assetClasses.forEach((aCls) => aCls.assets?.forEach((a) => this.k1isins.push(a.isin))),
      );
    }
    if (this.detailMode) {
      this.rootStore!.stores.uiStore.userMenuData.profileLockActive = !!this.data?.profileLockActive;
    }

    this.rootStore.stores.uiStore.setLoading(false);
    return this.data;
  };

  postInflation = async () => {
    const { apiStore } = this.rootStore.stores;
    return await apiStore.post<boolean>(`${this.inflationEndpoint(this.accountId)}`, {
      enabled: this.chartComputedData.inflation,
      rate: this.chartComputedData.inflationRate,
    });
  };

  setInflationRate = async (rate: number) => {
    this.chartComputedData.inflationRate = rate;
    const postResponse = (await this.postInflation()) as unknown as ApiResponse<{
      value: boolean;
    }>;
    if (postResponse?.data?.value) {
      this.resetCache();
      await this.fetchAccountPlan();
    }
  };

  switchInflation = async () => {
    this.chartComputedData.inflation = !this.chartComputedData.inflation;

    const postResponse = (await this.postInflation()) as unknown as ApiResponse<{
      value: boolean;
    }>;
    if (postResponse?.data?.value) {
      this.resetCache();
      await this.fetchAccountPlan();
    }
  };

  toggleExtProd = async (id: string, active: boolean) => {
    const { apiStore, uiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    const response = await apiStore.post<SingleValueResponse<boolean>>(`${this.extProdEndpoint(this.accountId, id)}`, {
      value: active,
    });
    if (response?.data?.value) {
      this.resetCache();
      await this.fetchAccountPlan();
    }
    uiStore.setLoading(false);
  };

  resetCache = () => {
    this.rootStore.stores.apiStore.removeFromCache(this.endpoint(this.accountId));
  };

  handleNext = (goFwd?: () => void) => {
    if (!!goFwd) {
      goFwd();
    } else {
      history.push(ACCOUNTS_FEES_EDIT(this.accountId));
    }
  };

  handleBack = () => {
    history.push(ACCOUNTS_EDIT_INVESTMENT(this.accountId));
  };

  @computed
  get pie(): PieSeriesData {
    if (!this.data || !this.activatedBucket || !this._activatedBucketType) return {};
    return convertPiechartData(
      this.activatedBucket?.portfolio?.chart?.dataset,
      this.activatedBucket.portfolio.code,
      this._activatedBucketType,
    );
  }

  // display: display date (true), otherwise end date
  setDisplayDate = async (date: string, display: boolean) => {
    const { post } = this.rootStore.stores.apiStore;
    const response = await post<SingleValueResponse<boolean>>(
      display ? this.displayDateEndpoint(this.accountId) : this.endDateEndpoint(this.accountId),
      {
        date,
      },
    );
    if (response?.data?.value) {
      this.resetCache();
    }
  };

  handleRiskChange = async (accountId: string, bucketType: string, profile: number) => {
    const { post } = this.rootStore.stores.apiStore;
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(this.riskEndpoint(accountId), {
      bucketType,
      profile,
    });
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.data?.value) {
      this.resetCache();
      await this.fetchAccountPlan();
    }
  };

  setPortfolioCode = async (portfolioCode: string, bucketType?: BucketType) => {
    const { post } = this.rootStore.stores.apiStore;
    const code = portfolioCode.split(',');
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(this.esgEndpoint(this.accountId), {
      code: { type: code[0], code: code[1] },
      bucketType,
    });
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.data?.value === true) {
      this.resetCache();
      await this.fetchAccountPlan();
    }
  };

  switchBucketSetting = async (setting: 'rebalancing' | 'hedging' | 'time', bucketType: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.rootStore.stores.uiStore.setLoading(true);

    let response = null;

    switch (setting) {
      case 'rebalancing':
        response = await post<SingleValueResponse<boolean>>(this.rebalancingEndpoint(this.accountId), {
          value: !this.data!.buckets[this.activatedBucketNumber!].automaticRebalancing,
          bucketType: bucketType,
        });
        break;
      case 'time':
        response = await post<SingleValueResponse<boolean>>(this.rebalancingTimeEndpoint(this.accountId), {
          value: !this.data!.buckets[this.activatedBucketNumber!].rebalancingTimeTest,
          bucketType: bucketType,
        });
        break;
      case 'hedging':
        response = await post<SingleValueResponse<boolean>>(this.hedgingEndpoint(this.accountId), {
          value: !this.data!.buckets[this.activatedBucketNumber!].currencyHedging,
          bucketType: bucketType,
        });
        break;
    }

    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.data?.value) {
      this.resetCache();
      await this.fetchAccountPlan();
    }
  };

  setOneTimeRebalancing = async (bucketType: string, rebalance: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post(
      API_ACCOUNT_DETAIL_PLAN_ONETIMEREBALANCE(this.accountId),
      { bucketType, value: rebalance },
    );
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.status === 200) {
      this.data!.buckets[this.data!.buckets.findIndex((b) => b.type === bucketType)].oneTimeRebalance = rebalance;
    }
    return response?.status === 200;
  };

  checkTaxresidency = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.patch<{ value: boolean }>(
      this.endpoint(this.accountId),
      undefined,
      undefined,
      true,
    );
    this.rootStore.stores.uiStore.setLoading(false);
    return response?.data?.value;
  };

  @action getRecommendedBucket = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    if (this.activatedBucket) {
      const response = await this.rootStore.stores.apiStore.get<Portfolio>(
        this.recommendedEndpoint(this.accountId, this.activatedBucket?.type),
      );
      if (response?.data) {
        this.portfolioComparison = makePortfolioComparison(
          [{ ...this.activatedBucket }],
          [{ portfolio: response.data }],
        );
      }
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action updateBucket = async () => {
    if (this.activatedBucket) {
      this.rootStore.stores.uiStore.setLoading(true);
      const response = await this.rootStore.stores.apiStore.post(this.updateEndpoint(this.accountId), {
        bucketType: this.activatedBucket.type,
      });
      this.rootStore.stores.uiStore.setLoading(false);
      if (response?.data) {
        this.fetchAccountPlan();
        return true;
      } else {
        return false;
      }
    }
  };

  @computed
  get activeBuckets() {
    return this.data?.buckets
      ?.filter((bucket: Bucket) => bucket.active)
      .slice()
      .reverse();
  }

  @computed
  get buckets() {
    return this.data?.buckets;
  }

  @computed
  get activatedBucket() {
    return this.activeBuckets?.find((bucket: Bucket) => this.activatedBucketType === bucket.type) || null;
  }

  @computed
  get activatedBucketType() {
    if (
      this._activatedBucketType &&
      this.activeBuckets &&
      this.activeBuckets?.findIndex((bucket) => bucket.type === this._activatedBucketType) > -1
    ) {
      return this._activatedBucketType;
    } else {
      return this.activeBuckets?.find((bucket: Bucket) => bucket.active)?.type || null;
    }
  }

  set activatedBucketType(bucketType: BucketType | null) {
    this._activatedBucketType = bucketType;
  }

  /**
   * Helper for getting index of activated Bucket from buckets.
   */
  @computed
  get activatedBucketNumber() {
    return this.buckets?.findIndex((bucket) => bucket.type === this.activatedBucketType);
  }

  // value?: number should be value: number, but the @material-ui typing is bugged
  @action
  setBucketTab = (_event: React.ChangeEvent<{}>, value?: number) => {
    if (value !== undefined) {
      this.bucketTab = value;
    }
  };
}
